<template>
  <Menu as="div" class="relative">
    <MenuButton  class="w-64 flex items-center justify-between px-3 py-2 text-sm font-medium rounded-lg hover:bg-secondary-50 dark:hover:bg-secondary-700/50 transition-colors duration-200 border border-secondary-200 dark:border-secondary-700">
      <div class="flex items-center gap-2 flex-1">
        <div v-if="currentCompany" class="flex h-6 w-6 items-center justify-center rounded-full bg-primary-600 text-xs font-medium text-white shrink-0">
          {{ currentCompany.name?.charAt(0).toUpperCase() }}
        </div>
        <BuildingOfficeIcon v-else class="h-5 w-5 text-secondary-400 shrink-0" />
        <span class="text-secondary-900 dark:text-white truncate">
          {{ currentCompany?.name || t('dashboard.header.companySelector.selectCompany') }}
        </span>
      </div>
      <div class="flex items-center gap-2">
        <button
          v-if="currentCompany && checkClientType(user?.clientType) && canCreate('users')"
          @click.stop="$emit('share')"
          class="p-1.5 rounded-md hover:bg-secondary-100 dark:hover:bg-secondary-600 text-secondary-500 hover:text-primary-500 dark:text-secondary-400 dark:hover:text-primary-400 transition-colors duration-200"
          :title="t('dashboard.header.companySelector.shareButton.title')"
        >
          <UserPlusIcon class="h-4 w-4" />
        </button>
        <ChevronDownIcon class="h-4 w-4 text-secondary-400" aria-hidden="true" />
      </div>
    </MenuButton>

    <transition
      enter-active-class="transition ease-out duration-100"
      enter-from-class="transform opacity-0 scale-95"
      enter-to-class="transform opacity-100 scale-100"
      leave-active-class="transition ease-in duration-75"
      leave-from-class="transform opacity-100 scale-100"
      leave-to-class="transform opacity-0 scale-95"
      @after-enter="focusSearchInput"
      @after-leave="searchQuery = ''"
    >
      <MenuItems class="absolute left-0 z-50 mt-1 w-64 origin-top-right rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none dark:bg-secondary-800 dark:ring-secondary-700">
        <div class="py-2">
          <!-- Acciones principales -->
          
          <div class="my-1 border-t border-secondary-100 dark:border-secondary-700" />

          <!-- Lista de compañías -->
          <div class="px-4 py-2">
            <p class="text-xs font-medium text-secondary-500 dark:text-secondary-400">
              {{ isSuperAdmin ? 'Todas las Compañías (SuperAdmin)' : t('company.yourCompanies') }}
            </p>
          </div>

          <div v-if="showSearch" class="px-4 pb-2">
            <div class="relative">
              <MagnifyingGlassIcon class="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-secondary-400" />
              <input
                ref="searchInputRef"
                v-model="searchQuery"
                type="text"
                :placeholder="t('company.searchPlaceholder')"
                class="w-full pl-8 pr-3 py-1.5 text-sm bg-secondary-50 dark:bg-secondary-900 border border-secondary-200 dark:border-secondary-700 rounded-lg text-secondary-900 dark:text-white placeholder-secondary-400 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                @keydown="handleSearchKeydown"
              />
            </div>
          </div>

          <div class="max-h-60 overflow-y-auto">
            <div
              v-if="filteredCompanies.length === 0"
              class="px-4 py-3 text-center text-sm text-secondary-400"
            >
              {{ t('company.noResults') }}
            </div>
            <MenuItem v-for="company in filteredCompanies" :key="company.id" v-slot="{ active }">
              <div class="flex items-center justify-between px-4 py-2 hover:bg-secondary-50 dark:hover:bg-secondary-700/50">
                <button
                  @click="handleCompanySelect(company.id)"
                  class="flex items-center flex-1 group"
                >
                  <div class="flex h-6 w-6 items-center justify-center rounded-full bg-primary-600 text-xs font-medium text-white">
                    {{ company.name?.charAt(0).toUpperCase() }}
                  </div>
                  <span 
                    :class="[
                      company.id === selectedCompanyId ? 'font-semibold text-primary-600 dark:text-primary-400' : 'font-medium text-secondary-900 dark:text-white',
                      'ml-3'
                    ]"
                  >
                    {{ company.name }}
                  </span>
                </button>
              </div>
            </MenuItem>
          </div>
        </div>
      </MenuItems>
    </transition>
  </Menu>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue'
import { ChevronDownIcon, UserPlusIcon, BuildingOfficeIcon, MagnifyingGlassIcon } from '@heroicons/vue/24/outline';
import { useAuthStore } from '@/stores/auth'
import { useCompanyStore } from '@/stores/company'
import { useI18n } from 'vue-i18n'
import { checkClientType } from '@/utils/checkClientType'
import { usePermissions } from '@/composables/usePermissions'

const router = useRouter()
const authStore = useAuthStore()
const companyStore = useCompanyStore()
const { t } = useI18n()
const { canCreate } = usePermissions()

defineEmits(['share'])

const companies = computed(() => companyStore.availableCompanies || [])
const searchQuery = ref('')
const searchInputRef = ref<HTMLInputElement | null>(null)
const showSearch = computed(() => companies.value.length > 6)
const filteredCompanies = computed(() => {
  const query = searchQuery.value.trim().toLowerCase()
  if (!query) return companies.value
  return companies.value.filter(company => company.name?.toLowerCase().includes(query))
})

const focusSearchInput = () => {
  if (showSearch.value) searchInputRef.value?.focus()
}

// Evita que el type-ahead de Headless UI Menu capture las teclas del input (Escape sigue cerrando el menú)
const handleSearchKeydown = (event: KeyboardEvent) => {
  if (event.key !== 'Escape' && event.key !== 'Tab') event.stopPropagation()
}
const currentCompany = computed(() => companyStore.getSelectedCompany())
const selectedCompanyId = computed(() => currentCompany.value?.id)
const isSuperAdmin = computed(() => companyStore.isSuperAdmin)
const user = computed(() => authStore.user)

const handleCompanySelect = async (companyId: string) => {
  if (companyId === selectedCompanyId.value) return
  await companyStore.setSelectedCompany(companyId)
}

const navigateToSettings = () => {
  router.push('/dashboard/settings')
}


</script>
